import React, { Component } from 'react'
import { MutationFn } from 'react-apollo'
import { FullBlockLoader, CircleLoader } from '@deal/components'
import { Experiment } from '@deal/experiment-js'
import {
  CreatePathFlowComponent,
  CreatePathFlowMutation,
  CreatePathFlowMutationVariables,
  CreatePathFlowInput,
  PathFlowFragment
} from '#src/generated/graphql'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { WithIdentityProps, withIdentity } from '#src/containers/Identity'
import { PathLayouts } from '#src/typings'
import { FlowState } from '../'
import pickBy from 'lodash/pickBy'
import { ifBridgeDefined } from '#src/services/dealNativeBridge'

export interface PathCategory {
  id: string
  slug: string | null
}

interface CreatePathProps {
  pathSlug?: string
  categorySlug?: string
  prefillData?: CreatePathFlowInput
  pathLayout?: PathLayouts
  onPathCompleted: (flow: PathFlowFragment) => void
  onPathCreated: ({ pathSlug, pageSlug, flowId }: FlowState, pathLayout: PathLayouts) => void
}

interface CreatePathFlowMutationComponentProps {
  pathSlug?: string
  categorySlug?: string
  pathLayout: PathLayouts
  onPathCreated: (response: CreatePathFlowMutation, pathLayout: PathLayouts) => void
  prefillData?: CreatePathFlowInput
}

class CreatePathFlowMutationComponent extends React.Component<
  CreatePathFlowMutationComponentProps
> {
  createPathMutation?: MutationFn<CreatePathFlowMutation, CreatePathFlowMutationVariables>
  uniqueDeviceId?: string
  applicationId?: string

  public componentDidMount() {
    let nativeAppData: {
      uniqueDeviceId?: string
      applicationId?: string
    } = {}

    ifBridgeDefined(bridge => {
      nativeAppData.uniqueDeviceId = bridge.deviceUniqueId
      nativeAppData.applicationId = bridge.appBundleId
    })

    /**
     * This lets us use the Mutation component instead of passing in a HOC to the CreatePathFlowMutationComponent
     * but the functionality is essentially the same. This also prevents the render method from triggering multiple
     * path creations by having the mutation in the mount. There's no reason we would be creating paths on SSR.
     */
    if (this.createPathMutation) {
      const { pathSlug, categorySlug, prefillData } = this.props

      let createCriteria = {}
      if (pathSlug) {
        createCriteria = {
          pathSchemaSlug: pathSlug
        }
      } else if (categorySlug) {
        createCriteria = {
          categorySlug
        }
      } else {
        throw new Error('No path slug or category slug passed when attempting to create a path')
      }

      const sanitizedPrefillData = pickBy(prefillData, v => v !== '')

      this.createPathMutation({
        variables: {
          input: {
            ...sanitizedPrefillData,
            ...createCriteria,
            uniqueDeviceId: this.uniqueDeviceId,
            applicationId: this.applicationId,
            timeZoneOffsetInMinutes: new Date().getTimezoneOffset()
          }
        }
      })
    }
  }

  public render() {
    const { onPathCreated, pathLayout } = this.props

    return (
      <CreatePathFlowComponent
        onCompleted={response => onPathCreated(response, pathLayout)}
        onError={error => {
          throw new Error(error.message)
        }}
      >
        {createPathFlow => {
          this.createPathMutation = createPathFlow

          return <FullBlockLoader loader={CircleLoader} />
        }}
      </CreatePathFlowComponent>
    )
  }
}

class CreatePath extends Component<CreatePathProps & WithAnalyticsProps & WithIdentityProps> {
  constructor(props: CreatePathProps & WithAnalyticsProps & WithIdentityProps) {
    super(props)

    this.handlePathCreated = this.handlePathCreated.bind(this)
  }

  /**
   * After creating the path:
   *
   *  1. Check if a new user was registered during path creation
   *    (1a) If so, update the user's identity on the client
   *    (1b) If so, and track the registration
   *
   *  2. Check if the path is already completed (possible if enough prefill data is passed)
   *    (2a) If so, short circuit and call the onPathCompleted callback
   *    (2b) Otherwise, call the onPathCreated callback
   */
  public handlePathCreated(response: CreatePathFlowMutation, pathLayout: PathLayouts) {
    const { createPathFlow } = response

    if (!createPathFlow.flow) {
      throw new Error('Error creating path flow')
    }

    const createdPathFlow = createPathFlow.flow

    let refetchIdentity: Promise<null> = Promise.resolve(null)

    // 1. Check if a user was registered during path creation
    if (createPathFlow.newUserRegistered && this.props.refetchIdentity) {
      // 1a. Update the user's identity on the client
      refetchIdentity = this.props.refetchIdentity().then(result => {
        const myself = result.data.me

        // 1b. Track it
        if (this.props.analytics && myself) {
          this.props.analytics.userRegistered({
            parent_category_slug: createdPathFlow.category && createdPathFlow.slug,
            first_name: myself.user.firstName || undefined,
            last_name: myself.user.lastName || undefined
          })
        }
        return null
      })
    }

    refetchIdentity.then(() => {
      // 2. Check if the path is already completed (possible if enough prefill data is passed)
      if (createdPathFlow.completed) {
        // 2b. Call onPathCompleted
        return this.props.onPathCompleted(createdPathFlow)
      }

      const flowSlug = createdPathFlow.slug
      const flowId = createdPathFlow.id
      const firstPageSlug = createdPathFlow.pagesConnection.edges[0]
        ? createdPathFlow.pagesConnection.edges[0].node.slug
        : undefined

      // 2b. Call onPathCreated
      this.props.onPathCreated(
        {
          pathSlug: flowSlug,
          pageSlug: firstPageSlug,
          flowId
        },
        pathLayout
      )
    })
  }

  /**
   * Derive the strict enum pathlayout from the passed in string
   */
  public derivePathLayout(maybePathLayout?: string) {
    switch (maybePathLayout) {
      case 'bubba':
        return PathLayouts.BUBBA

      case 'woods':
        return PathLayouts.WOODS

      case 'brooks':
        return PathLayouts.BROOKS

      case 'rory':
        return PathLayouts.RORY

      case 'default':
      default:
        return PathLayouts.DEFAULT
    }
  }

  public render() {
    const { pathSlug, categorySlug, prefillData } = this.props

    if (categorySlug) {
      /**
       * If it's a category slug, create the path with the category slug.
       */
      return (
        <Experiment
          id={`${categorySlug}-path-layout`}
          options={{
            skip: this.props.pathLayout !== undefined
          }}
          default="default"
        >
          {({ result: pathLayoutResult, loading }) => {
            if (loading) {
              return <FullBlockLoader loader={CircleLoader} />
            }

            /**
             * Honor the passed in path layout otherwise use the default
             * returned by the experiment client
             */
            let pathLayout
            if (this.props.pathLayout) {
              pathLayout = this.props.pathLayout
            } else {
              pathLayout = this.derivePathLayout(pathLayoutResult)
            }

            return (
              <CreatePathFlowMutationComponent
                onPathCreated={this.handlePathCreated}
                prefillData={prefillData}
                categorySlug={categorySlug}
                pathLayout={pathLayout}
              />
            )
          }}
        </Experiment>
      )
    } else if (pathSlug) {
      /**
       * If it's a path slug, just reference the path slug directly. We don't
       * experiment on the layout when the path is derived directly from a path
       * slug
       */
      return (
        <CreatePathFlowMutationComponent
          onPathCreated={this.handlePathCreated}
          prefillData={prefillData}
          pathSlug={pathSlug}
          pathLayout={this.props.pathLayout || PathLayouts.DEFAULT}
        />
      )
    }

    throw new Error('Insufficient parameters passed to create a new path')
  }
}

export default withAnalytics(withIdentity(CreatePath))
